import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const OrderViewModal = ({ isOpen, onClose, order, language, onEdit, onDelete }) => {
  if (!isOpen || !order) return null;

  const labels = {
    title: { en: 'Order Details', ar: 'تفاصيل الطلب' },
    orderId: { en: 'Order ID', ar: 'رقم الطلب' },
    customerInfo: { en: 'Customer Information', ar: 'معلومات العميل' },
    firstName: { en: 'First Name', ar: 'الاسم الأول' },
    lastName: { en: 'Last Name', ar: 'اسم العائلة' },
    phoneNumber: { en: 'Phone Number', ar: 'رقم الهاتف' },
    address: { en: 'Address', ar: 'العنوان' },
    locationInfo: { en: 'Location Information', ar: 'معلومات الموقع' },
    governorate: { en: 'Governorate', ar: 'المحافظة' },
    area: { en: 'Area', ar: 'المنطقة' },
    orderInfo: { en: 'Order Information', ar: 'معلومات الطلب' },
    orderDate: { en: 'Order Date', ar: 'تاريخ الطلب' },
    status: { en: 'Status', ar: 'الحالة' },
    type: { en: 'Type', ar: 'النوع' },
    items: { en: 'Order Items', ar: 'عناصر الطلب' },
    productName: { en: 'Product Name', ar: 'اسم المنتج' },
    quantity: { en: 'Quantity', ar: 'الكمية' },
    price: { en: 'Price', ar: 'السعر' },
    subtotal: { en: 'Subtotal', ar: 'المجموع الفرعي' },
    totalAmount: { en: 'Total Amount', ar: 'المبلغ الإجمالي' },
    discount: { en: 'Discount', ar: 'الخصم' },
    notes: { en: 'Notes', ar: 'ملاحظات' },
    noNotes: { en: 'No notes', ar: 'لا توجد ملاحظات' },
    edit: { en: 'Edit', ar: 'تعديل' },
    delete: { en: 'Delete', ar: 'حذف' },
    close: { en: 'Close', ar: 'إغلاق' },
    created: { en: 'Created', ar: 'تم الإنشاء' },
    inProgress: { en: 'In Progress', ar: 'قيد التنفيذ' },
    completed: { en: 'Completed', ar: 'مكتمل' },
    cancelled: { en: 'Cancelled', ar: 'ملغي' },
    direct: { en: 'Direct', ar: 'مباشر' },
    scheduled: { en: 'Scheduled', ar: 'مجدول' }
  };

  const getStatusColor = (status) => {
    const colors = {
      CREATED: 'bg-blue-100 text-blue-800',
      IN_PROGRESS: 'bg-yellow-100 text-yellow-800',
      COMPLETED: 'bg-green-100 text-green-800',
      CANCELLED: 'bg-red-100 text-red-800'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      CREATED: labels?.created?.[language],
      IN_PROGRESS: labels?.inProgress?.[language],
      COMPLETED: labels?.completed?.[language],
      CANCELLED: labels?.cancelled?.[language]
    };
    return statusMap?.[status] || status;
  };

  const getTypeLabel = (type) => {
    const typeMap = {
      DIRECT: labels?.direct?.[language],
      SCHEDULED: labels?.scheduled?.[language]
    };
    return typeMap?.[type] || type;
  };

  return (
     <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-4 border border-border w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-foreground">
            {labels?.title?.[language]}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-smooth"
            aria-label="Close"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-secondary mb-1">{labels?.orderId?.[language]}</p>
              <p className="text-2xl font-bold text-foreground">#{order?.id}</p>
            </div>
            <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(order?.orderStatusName)}`}>
              {getStatusLabel(order?.order_status_name)}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground border-b border-border pb-2">
                {labels?.customerInfo?.[language]}
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-text-secondary">{labels?.firstName?.[language]}</p>
                  <p className="text-base font-medium text-foreground">{order?.first_name}</p>
                </div>
                <div>
                  <p className="text-sm text-text-secondary">{labels?.lastName?.[language]}</p>
                  <p className="text-base font-medium text-foreground">{order?.last_name}</p>
                </div>
                <div>
                  <p className="text-sm text-text-secondary">{labels?.phoneNumber?.[language]}</p>
                  <p className="text-base font-medium text-foreground">{order?.phone_number}</p>
                </div>
                <div>
                  <p className="text-sm text-text-secondary">{labels?.address?.[language]}</p>
                  <p className="text-base font-medium text-foreground">{order?.address}</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground border-b border-border pb-2">
                {labels?.locationInfo?.[language]}
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-text-secondary">{labels?.governorate?.[language]}</p>
                  <p className="text-base font-medium text-foreground">{order?.area?.governorate_name}</p>
                </div>
                <div>
                  <p className="text-sm text-text-secondary">{labels?.area?.[language]}</p>
                  <p className="text-base font-medium text-foreground">{order?.area?.name}</p>
                </div>
              </div>

              <h3 className="text-lg font-semibold text-foreground border-b border-border pb-2 mt-6">
                {labels?.orderInfo?.[language]}
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-text-secondary">{labels?.orderDate?.[language]}</p>
                  <p className="text-base font-medium text-foreground">
                    {new Date(order?.order_date)?.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-text-secondary">{labels?.type?.[language]}</p>
                  <p className="text-base font-medium text-foreground">{getTypeLabel(order?.order_type_name)}</p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-foreground border-b border-border pb-2 mb-4">
              {labels?.items?.[language]}
            </h3>
            <div className="space-y-3">
              {order?.items?.map((item) => (
                <div key={item?.id} className="bg-muted/50 rounded-lg p-4 border border-border">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-semibold text-foreground">{item?.product?.name}</p>
                      <p className="text-sm text-text-secondary mt-1">{item?.product?.description}</p>
                      <p className="text-xs text-text-secondary mt-1">SKU: {item?.product?.sku}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-text-secondary">{labels?.quantity?.[language]}: {item?.quantity}</p>
                      <p className="text-sm text-text-secondary">{labels?.price?.[language]}: {item?.product_price}</p>
                      <p className="text-base font-semibold text-primary mt-1">
                        {labels?.subtotal?.[language]}: {item?.subtotal}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-primary/10 rounded-lg p-4 border border-primary/20">
            <div className="flex justify-between items-center mb-2">
              <span className="text-base font-medium text-foreground">{labels?.discount?.[language]}</span>
              <span className="text-base font-semibold text-foreground">${order?.discount}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold text-foreground">{labels?.totalAmount?.[language]}</span>
              <span className="text-2xl font-bold text-primary">${order?.totalAmount}</span>
            </div>
          </div>

          {order?.notes && (
            <div>
              <h3 className="text-lg font-semibold text-foreground border-b border-border pb-2 mb-3">
                {labels?.notes?.[language]}
              </h3>
              <p className="text-base text-foreground bg-muted/50 rounded-lg p-4">{order?.notes}</p>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-card border-t border-border px-6 py-4 flex gap-3">
          <Button
            variant="outline"
            fullWidth
            onClick={onClose}
          >
            {labels?.close?.[language]}
          </Button>
          <Button
            variant="default"
            fullWidth
            onClick={() => {
              onClose();
              onEdit(order);
            }}
          >
            {labels?.edit?.[language]}
          </Button>
          <Button
            variant="destructive"
            fullWidth
            onClick={() => {
              onClose();
              onDelete(order);
            }}
          >
            {labels?.delete?.[language]}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OrderViewModal;